import type { ReactNode } from "react";
import { Toaster } from "react-hot-toast";

import { TooltipProvider } from "@/components/ui/tooltip";
import { useIsDesktop } from "@/hooks/useMediaQuery";
import { QueryProvider } from "@/providers/QueryProvider";
import { StoreProvider } from "@/providers/StoreProvider";
import { ThemeProvider } from "@/providers/ThemeProvider";

/**
 * Composes every app-wide provider in the one order that matters: `Store`
 * first, since `ThemeProvider` reads/writes Redux state and would have
 * nothing to connect to otherwise. `QueryProvider`, `TooltipProvider`, and
 * the toast layer don't depend on either, so their position relative to the
 * other two is free. `TooltipProvider` sits here rather than being mounted
 * per-tooltip so every `Tooltip` in the app shares one `delayDuration`
 * (docs/ui/design-system.md's "short delay to avoid flicker on incidental
 * mouse movement") instead of each header icon re-declaring it.
 *
 * This composition lives in `app/`, separate from `providers/`, per
 * docs/architecture/frontend-architecture.md's folder structure:
 * `providers/` holds each individual provider; `app/` is where the app
 * shell assembles them. `App.tsx` (src root) is the only thing that imports
 * this.
 */
export function AppProviders({ children }: { children: ReactNode }) {
  return (
    <StoreProvider>
      <ThemeProvider>
        <QueryProvider>
          <TooltipProvider delayDuration={300}>
            {children}
            <ToastLayer />
          </TooltipProvider>
        </QueryProvider>
      </ThemeProvider>
    </StoreProvider>
  );
}

/**
 * Mounts react-hot-toast's single render target. Position follows
 * docs/ui/design-system.md §16: top-center on desktop, bottom-center on
 * mobile so a toast never overlaps the (future) `BottomNav`/thumb zone.
 */
function ToastLayer() {
  const isDesktop = useIsDesktop();

  return <Toaster position={isDesktop ? "top-center" : "bottom-center"} />;
}
